import React, { useState, useEffect, useRef, useCallback, useMemo, useTransition, useDeferredValue } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';

const extractId = (val) => {
  if (!val) return null;
  if (typeof val === 'object' && val._id) return val._id;
  if (typeof val === 'string') return val;
  return null;
};

const getTieredRate = (days) => {
  if (days <= 0) return 0;
  if (days <= 14) return 14;
  return Math.min(days, 30);
};

const COLLATERAL_TYPES = [
  'Land Title Deed',
  'Vehicle Logbook',
  'Motorcycle Logbook',
  'Electronics',
  'Household Items',
  'Livestock',
  'Chattel',
  'Shares/Stocks',
  'Fixed Deposit',
  'Cash Savings',
  'Business Equipment',
  'Other',
];

// ═══════════════════════════════════════════════════════════════
// STATIC STYLES
// ═══════════════════════════════════════════════════════════════
const INPUT_STYLE = Object.freeze({
  width: '100%', padding: '10px 12px', borderRadius: '8px',
  border: '1px solid #d1d5db', fontSize: '14px', transition: 'all 0.2s ease',
  outline: 'none', background: '#fff'
});

const INPUT_DISABLED_STYLE = Object.freeze({
  ...INPUT_STYLE,
  background: '#f3f4f6',
  color: '#9ca3af',
  cursor: 'not-allowed',
  borderColor: '#e5e7eb',
});

const handleInputFocus = (e) => { e.target.style.borderColor = '#6366f1'; e.target.style.boxShadow = '0 0 0 3px rgba(99, 102, 241, 0.1)'; };
const handleInputBlur = (e) => { e.target.style.borderColor = '#d1d5db'; e.target.style.boxShadow = 'none'; };

const ITEM_CARD_STYLE = Object.freeze({ background: '#f8fafc', borderRadius: 10, padding: 14, marginBottom: 10, border: '1px solid #e5e7eb' });
const SECTION_HEADER_STYLE = Object.freeze({ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, paddingBottom: 8, borderBottom: '2px solid #e5e7eb' });
const ADD_BTN_BASE = Object.freeze({ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 14px', background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)', color: '#fff', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600 });
const REMOVE_BTN_STYLE = Object.freeze({ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 28, height: 28, borderRadius: 6, background: '#fef2f2', border: '1px solid #fecaca', color: '#dc2626', fontSize: 14, cursor: 'pointer' });
const DROPDOWN_STYLE = Object.freeze({
  position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 50,
  background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8,
  boxShadow: '0 10px 25px rgba(0,0,0,0.12)', marginTop: 4,
  maxHeight: '320px', overflowY: 'auto',
});

// ═══════════════════════════════════════════════════════════════
// ACTIVE LOAN BLOCKER STYLES
// ═══════════════════════════════════════════════════════════════
const BLOCKER_STYLE = Object.freeze({
  background: 'linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%)',
  border: '2px solid #ef4444',
  borderRadius: 12,
  padding: 24,
  marginBottom: 20,
  textAlign: 'center',
});

const BLOCKER_ICON_STYLE = Object.freeze({
  width: 64,
  height: 64,
  borderRadius: '50%',
  background: '#fef2f2',
  border: '3px solid #ef4444',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: 32,
  margin: '0 auto 16px',
});

const BLOCKER_LOAN_CARD_STYLE = Object.freeze({
  background: '#fff',
  borderRadius: 10,
  padding: 16,
  marginTop: 16,
  border: '1px solid #fecaca',
  textAlign: 'left',
});

// ═══════════════════════════════════════════════════════════════
// SEARCHABLE CLIENT DROPDOWN (memoized + deferred query)
// ═══════════════════════════════════════════════════════════════
const SearchableClientSelect = React.memo(function SearchableClientSelect({ clients, value, onChange, disabled, loading, clientHasActiveLoan }) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef(null);
  const listRef = useRef(null);
  const [highlightIndex, setHighlightIndex] = useState(-1);
  const deferredQuery = useDeferredValue(query);

  const selected = useMemo(
    () => clients.find(c => c._id === value) || null,
    [clients, value]
  );

  const filtered = useMemo(() => {
    if (!deferredQuery.trim()) return clients;
    const q = deferredQuery.toLowerCase();
    return clients.filter(c => {
      return (
        (c.clientNumber || '').toLowerCase().includes(q) ||
        `${c.firstName || ''} ${c.lastName || ''}`.toLowerCase().includes(q) ||
        (c.phone || '').includes(q)
      );
    });
  }, [clients, deferredQuery]);

  useEffect(() => {
    const handler = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
        setQuery('');
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  useEffect(() => {
    setHighlightIndex(-1);
  }, [deferredQuery]);

  const handlePick = useCallback((client) => {
    // ═══════════════════════════════════════════════════════════
    // CRITICAL: Prevent selecting a client with an active loan
    // ═══════════════════════════════════════════════════════════
    if (client.activeLoans && client.activeLoans > 0) {
      alert(`⚠️ Cannot select this client!\n\n${client.firstName} ${client.lastName} has ${client.activeLoans} active loan(s) that must be fully repaid before applying for a new one.\n\nOutstanding balance: KES ${(client.totalOutstanding || 0).toLocaleString()}\n\nPlease select a different client.`);
      return;
    }
    onChange(client._id);
    setOpen(false);
    setQuery('');
    setHighlightIndex(-1);
  }, [onChange]);

  const handleClear = useCallback((e) => {
    e.stopPropagation();
    onChange('');
    setQuery('');
    setOpen(false);
  }, [onChange]);

  const handleKeyDown = useCallback((e) => {
    if (!open) {
      if (e.key === 'ArrowDown' || e.key === 'Enter') {
        e.preventDefault();
        setOpen(true);
      }
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightIndex(prev => Math.min(prev + 1, filtered.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightIndex(prev => Math.max(prev - 1, 0));
    } else if (e.key === 'Enter' && highlightIndex >= 0 && filtered[highlightIndex]) {
      e.preventDefault();
      handlePick(filtered[highlightIndex]);
    } else if (e.key === 'Escape') {
      setOpen(false);
      setQuery('');
    }
  }, [open, filtered, highlightIndex, handlePick]);

  const handleInputChange = useCallback((e) => {
    setQuery(e.target.value);
    setOpen(prev => prev || true);
  }, []);

  const handleInputFocus = useCallback(() => {
    if (!disabled) setOpen(true);
  }, [disabled]);

  const showDropdown = open && !disabled;

  return (
    <div ref={wrapperRef} style={{ position: 'relative' }}>
      <div
        style={{
          display: 'flex', alignItems: 'center',
          background: '#fff', borderRadius: 8,
          border: open ? '2px solid #6366f1' : '1px solid #d1d5db',
          transition: 'all 0.2s ease',
          boxShadow: open ? '0 0 0 3px rgba(99,102,241,0.1)' : 'none',
        }}
      >
        <div style={{
          paddingLeft: 12, paddingRight: 6, color: selected ? '#059669' : '#9ca3af',
          fontSize: 15, flexShrink: 0, display: 'flex', alignItems: 'center',
        }}>
          {loading && value ? (
            <span style={{ animation: 'spin 1s linear infinite', display: 'inline-block' }}>⏳</span>
          ) : selected ? (clientHasActiveLoan ? '🚫' : '✅') : '🔍'}
        </div>

        {selected && !open ? (
          <div style={{
            flex: 1, display: 'flex', alignItems: 'center', gap: 8,
            padding: '10px 4px 10px 0', fontSize: 14, minWidth: 0,
          }}>
            <span style={{
              fontFamily: 'monospace', fontWeight: 700,
              color: clientHasActiveLoan ? '#dc2626' : '#1e40af',
              background: clientHasActiveLoan ? '#fee2e2' : '#dbeafe',
              padding: '2px 8px', borderRadius: 4,
              fontSize: 12, flexShrink: 0,
            }}>
              {selected.clientNumber}
            </span>
            <span style={{ color: '#111827', fontWeight: 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {selected.firstName} {selected.lastName}
            </span>
            {selected.phone && (
              <span style={{ color: '#6b7280', fontSize: 12, flexShrink: 0 }}>
                📞 {selected.phone}
              </span>
            )}
            {clientHasActiveLoan && (
              <span style={{
                fontSize: '10px', color: '#dc2626', background: '#fee2e2',
                padding: '2px 6px', borderRadius: 4, fontWeight: 700, flexShrink: 0,
                border: '1px solid #fecaca'
              }}>
                HAS ACTIVE LOAN
              </span>
            )}
          </div>
        ) : (
          <input
            type="text"
            value={query}
            onChange={handleInputChange}
            onFocus={handleInputFocus}
            onKeyDown={handleKeyDown}
            disabled={disabled}
            placeholder="Search by name, ID, or phone..."
            style={{
              flex: 1, border: 'none', outline: 'none', padding: '10px 4px',
              fontSize: 14, background: 'transparent', color: '#111827',
              minWidth: 0,
            }}
            autoComplete="off"
          />
        )}

        {selected && !open && (
          <button
            type="button" onClick={handleClear} disabled={disabled}
            style={{
              padding: '6px 10px', background: 'none', border: 'none',
              color: '#6b7280', cursor: disabled ? 'not-allowed' : 'pointer',
              fontSize: 16, lineHeight: 1, marginRight: 4, borderRadius: 4,
            }}
            title="Clear selection"
          >✕</button>
        )}
        {!selected && (
          <div style={{
            padding: '6px 10px', color: '#9ca3af', fontSize: 12, flexShrink: 0,
          }}>
            {filtered.length} client{filtered.length !== 1 ? 's' : ''}
          </div>
        )}
      </div>

      <input type="hidden" name="client_required" value={value || ''} />

      {showDropdown && (
        <div ref={listRef} style={DROPDOWN_STYLE}>
          {filtered.length === 0 ? (
            <div style={{ padding: '20px 16px', textAlign: 'center', color: '#9ca3af', fontSize: 13 }}>
              <div style={{ fontSize: 24, marginBottom: 6 }}>🔍</div>
              No clients match "<strong>{deferredQuery}</strong>"
            </div>
          ) : (
            <>
              {deferredQuery.trim() && (
                <div style={{
                  padding: '6px 14px', fontSize: 11, color: '#6b7280',
                  background: '#f9fafb', borderBottom: '1px solid #f3f4f6', fontWeight: 600,
                }}>
                  Showing {filtered.length} of {clients.length} clients
                </div>
              )}
              {filtered.map((c, idx) => {
                const hasActiveLoan = c.activeLoans && c.activeLoans > 0;
                return (
                  <div
                    key={c._id}
                    onClick={() => handlePick(c)}
                    onMouseEnter={() => setHighlightIndex(idx)}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 10,
                      padding: '10px 14px', cursor: hasActiveLoan ? 'not-allowed' : 'pointer',
                      background: idx === highlightIndex ? '#eef2ff' : (hasActiveLoan ? '#fef2f2' : (idx % 2 === 0 ? '#fff' : '#f9fafb')),
                      borderBottom: '1px solid #f3f4f6',
                      transition: 'background 0.1s ease',
                      opacity: hasActiveLoan ? 0.6 : 1,
                    }}
                    title={hasActiveLoan ? `Cannot select: Has ${c.activeLoans} active loan(s)` : `Select ${c.firstName} ${c.lastName}`}
                  >
                    <span style={{
                      fontFamily: 'monospace', fontWeight: 700, fontSize: 11,
                      color: hasActiveLoan ? '#dc2626' : '#4f46e5',
                      background: hasActiveLoan ? '#fee2e2' : '#eef2ff',
                      padding: '2px 7px', borderRadius: 4, flexShrink: 0,
                    }}>
                      {c.clientNumber}
                    </span>
                    <span style={{ flex: 1, fontSize: 13, color: '#111827', fontWeight: 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {c.firstName} {c.lastName}
                    </span>
                    <span style={{ fontSize: 12, color: '#6b7280', flexShrink: 0 }}>
                      📞 {c.phone}
                    </span>
                    {hasActiveLoan && (
                      <span style={{
                        fontSize: '10px', color: '#dc2626', background: '#fee2e2',
                        padding: '2px 6px', borderRadius: 4, fontWeight: 700, flexShrink: 0,
                        border: '1px solid #fecaca'
                      }}>
                        🚫 ACTIVE LOAN
                      </span>
                    )}
                  </div>
                );
              })}
              {filtered.length > 50 && (
                <div style={{
                  padding: '8px 14px', textAlign: 'center', fontSize: 11,
                  color: '#9ca3af', background: '#f9fafb', borderTop: '1px solid #f3f4f6',
                }}>
                  Type more to narrow down {filtered.length} results...
                </div>
              )}
            </>
          )}
        </div>
      )}

      {loading && value && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, right: 0,
          padding: '8px 14px', fontSize: 12, color: '#6366f1',
          background: '#eef2ff', borderRadius: '0 0 8px 8px',
          border: '1px solid #c7d2fe', borderTop: 'none',
          display: 'flex', alignItems: 'center', gap: 6,
        }}>
          <span style={{ animation: 'spin 1s linear infinite' }}>⏳</span>
          Checking active loans & fetching onboarding data...
        </div>
      )}
    </div>
  );
});

// ═══════════════════════════════════════════════════════════════
// MEMOIZED COLLATERAL ITEM
// ═══════════════════════════════════════════════════════════════
const CollateralItem = React.memo(function CollateralItem({ item, index, onChange, onImageChange, onRemove, canRemove, isFromOnboarding, isDisabled }) {
  return (
    <div style={ITEM_CARD_STYLE}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
        <span style={{ fontSize: 12, fontWeight: 600, color: '#6366f1', background: '#eef2ff', padding: '2px 10px', borderRadius: 12 }}>
          Collateral {index + 1}
        </span>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          {isFromOnboarding && (
            <span style={{ fontSize: '10px', color: '#059669', background: '#ecfdf5', padding: '2px 6px', borderRadius: '4px', fontWeight: 600 }}>
              FROM ONBOARDING
            </span>
          )}
          {canRemove && !isDisabled && (
            <button type="button" onClick={() => onRemove(index)} style={REMOVE_BTN_STYLE}>✕</button>
          )}
        </div>
      </div>
      <div className="grid grid-2 gap-1">
        <div className="form-group">
          <label style={{ fontSize: 12 }}>Collateral Type*</label>
          <select
            className="form-control"
            required
            value={item.type}
            onChange={e => onChange(index, 'type', e.target.value)}
            disabled={isDisabled}
            style={{
              ...(isDisabled ? INPUT_DISABLED_STYLE : INPUT_STYLE),
              background: isFromOnboarding ? '#f0fdf4' : (isDisabled ? '#f3f4f6' : '#fff'),
              borderColor: isFromOnboarding ? '#86efac' : '#d1d5db',
            }}
            onFocus={isDisabled ? undefined : handleInputFocus}
            onBlur={isDisabled ? undefined : handleInputBlur}
          >
            <option value="">Select Type</option>
            {COLLATERAL_TYPES.map(t => (
              <option key={t} value={t}>{t}</option>
            ))}
          </select>
        </div>
        <div className="form-group">
          <label style={{ fontSize: 12 }}>Estimated Value (KES)*</label>
          <input
            type="number"
            className="form-control"
            required
            placeholder="e.g. 50000"
            value={item.estimatedValue}
            onChange={e => onChange(index, 'estimatedValue', e.target.value)}
            disabled={isDisabled}
            style={{
              ...(isDisabled ? INPUT_DISABLED_STYLE : INPUT_STYLE),
              background: isFromOnboarding ? '#f0fdf4' : (isDisabled ? '#f3f4f6' : '#fff'),
              borderColor: isFromOnboarding ? '#86efac' : '#d1d5db',
            }}
            onFocus={isDisabled ? undefined : handleInputFocus}
            onBlur={isDisabled ? undefined : handleInputBlur}
            min="0"
          />
        </div>
      </div>
      <div className="form-group" style={{ marginTop: 8 }}>
        <label style={{ fontSize: 12 }}>Description</label>
        <input
          className="form-control"
          placeholder="Brief description of the collateral"
          value={item.description}
          onChange={e => onChange(index, 'description', e.target.value)}
          disabled={isDisabled}
          style={{
            ...(isDisabled ? INPUT_DISABLED_STYLE : INPUT_STYLE),
            background: isFromOnboarding ? '#f0fdf4' : (isDisabled ? '#f3f4f6' : '#fff'),
            borderColor: isFromOnboarding ? '#86efac' : '#d1d5db',
          }}
          onFocus={isDisabled ? undefined : handleInputFocus}
          onBlur={isDisabled ? undefined : handleInputBlur}
        />
      </div>
      <div className="form-group" style={{ marginTop: 8 }}>
        <label style={{ fontSize: 12 }}>Collateral Photo</label>
        {item.image ? (
          <div style={{ position: 'relative', display: 'inline-block', marginTop: 4 }}>
            <img
              src={item.image}
              alt="Collateral"
              style={{ width: 120, height: 90, objectFit: 'cover', borderRadius: 8, border: '2px solid #e5e7eb', opacity: isDisabled ? 0.6 : 1 }}
            />
            {!isDisabled && (
              <button
                type="button"
                onClick={() => onChange(index, 'image', null)}
                style={{
                  position: 'absolute', top: -6, right: -6,
                  width: 22, height: 22, borderRadius: '50%',
                  background: '#ef4444', color: '#fff', border: '2px solid #fff',
                  fontSize: 11, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                  lineHeight: 1,
                }}
              >✕</button>
            )}
            {isFromOnboarding && (
              <span style={{ display: 'block', fontSize: 10, color: '#059669', marginTop: 2, textAlign: 'center' }}>📎 Pulled from onboarding</span>
            )}
          </div>
        ) : (
          <div style={{ marginTop: 4 }}>
            <label style={({
              display: 'inline-flex', alignItems: 'center', gap: 6,
              padding: '8px 16px', borderRadius: 8, border: '2px dashed #d1d5db',
              cursor: isDisabled ? 'not-allowed' : 'pointer', fontSize: 12, color: '#6b7280',
              transition: 'all 0.2s ease', opacity: isDisabled ? 0.5 : 1,
            })}
              onMouseEnter={isDisabled ? undefined : (e => { e.currentTarget.style.borderColor = '#6366f1'; e.currentTarget.style.color = '#6366f1'; e.currentTarget.style.background = '#eef2ff'; })}
              onMouseLeave={isDisabled ? undefined : (e => { e.currentTarget.style.borderColor = '#d1d5db'; e.currentTarget.style.color = '#6b7280'; e.currentTarget.style.background = 'transparent'; })}
            >
              📷 Upload Photo
              <input
                type="file"
                accept="image/*"
                onChange={e => onImageChange(index, e)}
                style={{ display: 'none' }}
                disabled={isDisabled}
              />
            </label>
          </div>
        )}
      </div>
    </div>
  );
});

// ═══════════════════════════════════════════════════════════════
// MEMOIZED GUARANTOR ITEM — National ID is OPTIONAL
// ═══════════════════════════════════════════════════════════════
const GuarantorItem = React.memo(function GuarantorItem({ guarantor, index, onChange, onRemove, canRemove, isFromOnboarding, isDisabled }) {
  const isOnboarding = isFromOnboarding && guarantor.name;
  const inputStyle = isDisabled ? INPUT_DISABLED_STYLE : INPUT_STYLE;
  const bgStyle = isOnboarding ? '#f0fdf4' : (isDisabled ? '#f3f4f6' : '#fff');
  const borderStyle = isOnboarding ? '#86efac' : '#d1d5db';

  return (
    <div style={{
      background: isOnboarding ? '#f0fdf4' : '#f8fafc',
      borderRadius: 10, padding: 14, marginBottom: 10,
      border: `1px solid ${isOnboarding ? '#86efac' : '#e5e7eb'}`,
      position: 'relative',
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 12, fontWeight: 600, color: '#6366f1', background: '#eef2ff', padding: '2px 10px', borderRadius: 12 }}>
            Guarantor {index + 1}
          </span>
          {isOnboarding && (
            <span style={{ fontSize: '10px', color: '#059669', background: '#ecfdf5', padding: '2px 6px', borderRadius: '4px', fontWeight: 600 }}>
              FROM ONBOARDING
            </span>
          )}
        </div>
        {canRemove && !isDisabled && (
          <button type="button" onClick={() => onRemove(index)} style={REMOVE_BTN_STYLE}>✕</button>
        )}
      </div>

      <div className="grid grid-2 gap-1">
        <div className="form-group">
          <label style={{ fontSize: 12 }}>Full Name*</label>
          <input
            type="text"
            className="form-control"
            required
            placeholder="e.g. John Doe"
            value={guarantor.name}
            onChange={e => onChange(index, 'name', e.target.value)}
            disabled={isDisabled}
            style={{ ...inputStyle, background: bgStyle, borderColor: borderStyle }}
            onFocus={isDisabled ? undefined : handleInputFocus}
            onBlur={isDisabled ? undefined : handleInputBlur}
          />
        </div>
        <div className="form-group">
          <label style={{ fontSize: 12 }}>Phone Number*</label>
          <input
            type="tel"
            className="form-control"
            required
            placeholder="e.g. 0712345678"
            value={guarantor.phone}
            onChange={e => onChange(index, 'phone', e.target.value)}
            disabled={isDisabled}
            style={{ ...inputStyle, background: bgStyle, borderColor: borderStyle }}
            onFocus={isDisabled ? undefined : handleInputFocus}
            onBlur={isDisabled ? undefined : handleInputBlur}
          />
        </div>
      </div>

      <div className="grid grid-2 gap-1" style={{ marginTop: 8 }}>
        <div className="form-group">
          {/*
            ═══════════════════════════════════════════════════════════
            NATIONAL ID IS NOW OPTIONAL — no `required`, no asterisk
            ═══════════════════════════════════════════════════════════
          */}
          <label style={{ fontSize: 12, color: '#6b7280' }}>
            National ID
            <span style={{ fontSize: 10, color: '#9ca3af', marginLeft: 4, fontWeight: 400 }}>(optional)</span>
          </label>
          <input
            type="text"
            className="form-control"
            placeholder="e.g. 12345678"
            value={guarantor.nationalId}
            onChange={e => onChange(index, 'nationalId', e.target.value)}
            disabled={isDisabled}
            style={{ ...inputStyle, background: bgStyle, borderColor: borderStyle }}
            onFocus={isDisabled ? undefined : handleInputFocus}
            onBlur={isDisabled ? undefined : handleInputBlur}
          />
        </div>
        <div className="form-group">
          <label style={{ fontSize: 12 }}>Relationship*</label>
          <input
            type="text"
            className="form-control"
            required
            placeholder="e.g. Friend, Relative"
            value={guarantor.relationship}
            onChange={e => onChange(index, 'relationship', e.target.value)}
            disabled={isDisabled}
            style={{ ...inputStyle, background: bgStyle, borderColor: borderStyle }}
            onFocus={isDisabled ? undefined : handleInputFocus}
            onBlur={isDisabled ? undefined : handleInputBlur}
          />
        </div>
      </div>
    </div>
  );
});

// ═══════════════════════════════════════════════════════════════
// ACTIVE LOAN BLOCKER COMPONENT
// ═══════════════════════════════════════════════════════════════
const ActiveLoanBlocker = React.memo(({ client, activeLoan }) => (
  <div style={BLOCKER_STYLE}>
    <div style={BLOCKER_ICON_STYLE}>🚫</div>
    <h3 style={{ color: '#991b1b', margin: '0 0 8px 0', fontSize: 18 }}>Client Has an Active Loan</h3>
    <p style={{ color: '#7f1d1d', margin: '0 0 16px 0', fontSize: 14, lineHeight: 1.6 }}>
      <strong>{client?.firstName} {client?.lastName}</strong> ({client?.clientNumber}) already has an active loan 
      that must be <strong>fully repaid</strong> before they can apply for a new one.
    </p>
    
    {activeLoan && (
      <div style={BLOCKER_LOAN_CARD_STYLE}>
        <div style={{ fontSize: 12, fontWeight: 600, color: '#991b1b', marginBottom: 12, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
          Active Loan Details
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <div>
            <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 2 }}>Loan Number</div>
            <div style={{ fontFamily: 'monospace', fontWeight: 700, fontSize: 13, color: '#1e40af' }}>
              {activeLoan.loanNumber || '—'}
            </div>
          </div>
          <div>
            <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 2 }}>Product</div>
            <div style={{ fontWeight: 600, fontSize: 13, color: '#111827' }}>
              {activeLoan.productName || '—'}
            </div>
          </div>
          <div>
            <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 2 }}>Principal Amount</div>
            <div style={{ fontWeight: 600, fontSize: 13, color: '#111827' }}>
              KES {(activeLoan.principalAmount || 0).toLocaleString()}
            </div>
          </div>
          <div>
            <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 2 }}>Outstanding Balance</div>
            <div style={{ fontWeight: 700, fontSize: 13, color: '#dc2626' }}>
              KES {(activeLoan.totalOutstanding || 0).toLocaleString()}
            </div>
          </div>
          <div>
            <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 2 }}>Disbursement Date</div>
            <div style={{ fontWeight: 500, fontSize: 13, color: '#111827' }}>
              {activeLoan.disbursementDate ? new Date(activeLoan.disbursementDate).toLocaleDateString() : '—'}
            </div>
          </div>
          <div>
            <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 2 }}>Days Active</div>
            <div style={{ fontWeight: 500, fontSize: 13, color: '#f59e0b' }}>
              {activeLoan.daysActive || 0} days
            </div>
          </div>
        </div>
        
        <div style={{ 
          marginTop: 16, 
          padding: '12px 16px', 
          background: '#fffbeb', 
          borderRadius: 8, 
          border: '1px solid #fcd34d',
          fontSize: 12,
          color: '#92400e'
        }}>
          <strong>⚠️ What to do:</strong>
          <ul style={{ margin: '8px 0 0', paddingLeft: 20, lineHeight: 1.8 }}>
            <li>Navigate to the <strong>Repayments</strong> module</li>
            <li>Record a full repayment for the outstanding balance</li>
            <li>Once the loan is <strong>closed</strong> (balance = 0), the client can apply again</li>
            <li>Or select a different client from the dropdown</li>
          </ul>
        </div>
      </div>
    )}
    
    <button
      type="button"
      onClick={() => window.location.reload()}
      style={{
        marginTop: 16,
        padding: '10px 24px',
        background: '#f3f4f6',
        color: '#374151',
        border: '1px solid #d1d5db',
        borderRadius: 8,
        fontSize: 13,
        fontWeight: 500,
        cursor: 'pointer',
      }}
    >
      🔄 Select a Different Client
    </button>
  </div>
));

// ═══════════════════════════════════════════════════════════════

const EMPTY_GUARANTOR = Object.freeze({ name: '', phone: '', nationalId: '', relationship: '' });
const EMPTY_COLLATERAL = Object.freeze({ type: '', description: '', estimatedValue: '', image: null });
const INITIAL_GUARANTORS = Object.freeze([EMPTY_GUARANTOR, EMPTY_GUARANTOR, EMPTY_GUARANTOR]);
const INITIAL_COLLATERAL = Object.freeze([{ ...EMPTY_COLLATERAL }]);

export default function CreateLoanApplication() {
  const [clients, setClients] = useState([]);
  const [products, setProducts] = useState([]);
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  // useTransition for non-urgent UI updates (auto-fill data display)
  const [isPending, startTransition] = useTransition();

  const [selectedClientDetails, setSelectedClientDetails] = useState(null);
  const [clientHasActiveLoan, setClientHasActiveLoan] = useState(false);
  const [activeLoanDetails, setActiveLoanDetails] = useState(null);
  const [autoFillMessage, setAutoFillMessage] = useState('');
  const [autoFillDetails, setAutoFillDetails] = useState([]);
  const [onboardingRefs, setOnboardingRefs] = useState([]);
  const [onboardingCollateral, setOnboardingCollateral] = useState([]);

  const [step, setStep] = useState(1);
  const [applicationId, setApplicationId] = useState(null);

  const [form, setForm] = useState({
    client: '',
    loanProduct: '',
    requestedAmount: '',
    duration: '',
    purpose: '', // Always empty — officer enters manually
    branch: '',
    guarantors: [EMPTY_GUARANTOR, EMPTY_GUARANTOR, EMPTY_GUARANTOR].map(g => ({ ...g })),
    collateral: [{ ...EMPTY_COLLATERAL }],
    businessPhotos: [],
    businessLocation: null
  });

  // ═══════════════════════════════════════════════════════════
  // Load initial data — parallel with Promise.all
  // ═══════════════════════════════════════════════════════════
  useEffect(() => {
    let cancelled = false;
    Promise.all([
      api.get('/clients?status=approved&limit=500').catch(() => ({ data: { data: { clients: [] } } })),
      api.get('/loan-products?isActive=true').catch(() => ({ data: { data: [] } })),
      api.get('/branches').catch(() => ({ data: { data: [] } })),
    ]).then(([clientsRes, productsRes, branchesRes]) => {
      if (cancelled) return;
      if (clientsRes.data?.data?.clients) setClients(clientsRes.data.data.clients);
      if (productsRes.data?.data) setProducts(Array.isArray(productsRes.data.data) ? productsRes.data.data : []);
      if (branchesRes.data?.data) setBranches(branchesRes.data.data);
    });
    return () => { cancelled = true; };
  }, []);

  // ═══════════════════════════════════════════════════════════
  // Derived: selected product
  // ═══════════════════════════════════════════════════════════
  const selectedProduct = useMemo(() => {
    if (!form.loanProduct) return null;
    return products.find(p => p._id === form.loanProduct) || null;
  }, [form.loanProduct, products]);

  const clientLoanLimit = useMemo(() => {
    const custom = Number(selectedClientDetails?.customLoanLimit || 0);
    const recommended = Number(selectedClientDetails?.recommendedLoanLimit || 0);
    return custom > 0 ? custom : recommended > 0 ? recommended : 0;
  }, [selectedClientDetails]);

  // ═══════════════════════════════════════════════════════════
  // Derived: interest & fee calculations
  // ═══════════════════════════════════════════════════════════
  const loanCalcs = useMemo(() => {
    const dur = parseInt(form.duration) || 0;
    const amt = parseFloat(form.requestedAmount) || 0;
    const rate = selectedProduct && dur > 0 ? getTieredRate(dur) : 0;
    const interest = amt * (rate / 100);
    const total = amt + interest;
    const procFee = amt * ((parseFloat(selectedProduct?.processingFeePercentage) || 0) / 100);
    const insFee = amt * ((parseFloat(selectedProduct?.insuranceFeePercentage) || 0) / 100);
    const net = Math.max(0, amt - procFee - insFee);
    return {
      duration: dur, requestedAmount: amt, interestRateAtMaturity: rate,
      estimatedInterest: interest, estimatedTotalRepayable: total,
      processingFee: procFee, insuranceFee: insFee, netDisbursed: net,
    };
  }, [form.duration, form.requestedAmount, selectedProduct]);

  // ═══════════════════════════════════════════════════════════
  // CLIENT SELECT — fetch details & auto-populate (OPTIMIZED)
  // ═══════════════════════════════════════════════════════════
  const handleClientSelect = useCallback(async (clientId) => {
    // Immediate reset of critical state
    setForm(prev => ({
      ...prev,
      client: clientId,
      requestedAmount: '',
      loanProduct: '',
      purpose: '', // Always empty — officer enters manually
      guarantors: INITIAL_GUARANTORS.map(g => ({ ...g })),
      collateral: INITIAL_COLLATERAL.map(c => ({ ...c })),
      businessPhotos: [],
      businessLocation: null
    }));
    setSelectedClientDetails(null);
    setAutoFillMessage('');
    setAutoFillDetails([]);
    setOnboardingRefs([]);
    setOnboardingCollateral([]);
    setApplicationId(null);
    setClientHasActiveLoan(false);
    setActiveLoanDetails(null);

    if (!clientId) return;

    try {
      setLoading(true);

      // ── PARALLEL: fetch client details + check active loan ──
      const [clientRes, activeLoanRes] = await Promise.all([
        api.get(`/clients/${clientId}`),
        // ═══════════════════════════════════════════════════════════
        // CRITICAL: Check for active loans
        // A client with activeLoans > 0 CANNOT apply for a new loan
        // ═══════════════════════════════════════════════════════════
        api.get(`/loans?client=${clientId}&status=active,overdue&limit=1`).catch(() => ({ data: { data: { loans: [] } } })),
      ]);

      const client = clientRes.data.data;

      // Set client details immediately (urgent)
      setSelectedClientDetails(client);

      // ═══════════════════════════════════════════════════════════
      // CRITICAL CHECK: Does this client have an active loan?
      // Check both the client.activeLoans field AND the API response
      // ═══════════════════════════════════════════════════════════
      const activeLoansFromClient = client.activeLoans || 0;
      const activeLoansFromApi = activeLoanRes.data?.data?.loans?.length || 0;
      const hasActiveLoan = activeLoansFromClient > 0 || activeLoansFromApi > 0;

      if (hasActiveLoan) {
        setClientHasActiveLoan(true);
        
        // Get the active loan details for display
        if (activeLoansFromApi > 0) {
          const loan = activeLoanRes.data.data.loans[0];
          setActiveLoanDetails({
            loanNumber: loan.loanNumber,
            principalAmount: loan.principalAmount,
            totalOutstanding: loan.totalOutstanding,
            disbursementDate: loan.disbursementDate,
            daysActive: loan.daysSinceDisbursement,
            productName: loan.loanProduct?.name || loan.loanProductName || '—',
          });
        } else {
          // Use client data for basic info
          setActiveLoanDetails({
            loanNumber: '—',
            principalAmount: client.totalBorrowed || 0,
            totalOutstanding: client.totalOutstanding || 0,
            disbursementDate: null,
            daysActive: null,
            productName: '—',
          });
        }

        // Stop here — don't auto-fill anything
        setLoading(false);
        return;
      }

      setClientHasActiveLoan(false);

      const details = [];
      let guarantors = [];
      let collateral = [{ ...EMPTY_COLLATERAL }];
      let collateralFromOnboarding = [];
      let businessPhotos = [];
      let businessLocation = null;
      let autoBranch = '';
      let suggestedAmount = '';
      let autoProduct = '';

      // ─── SOURCE 1: client.references ───
      if (client.references && client.references.length > 0) {
        const validRefs = client.references.filter(r => r.name);
        if (validRefs.length > 0) {
          guarantors = validRefs.map(ref => ({
            name: ref.name || '',
            phone: ref.phone || '',
            nationalId: ref.idNumber || ref.nationalId || '',  // Optional — may be empty
            relationship: ref.relationship || '',
          }));
          details.push(`${guarantors.length} guarantor(s) from client references`);
        }
      }

      // ─── SOURCE 2: Onboarding draft ───
      const leadId = extractId(client.convertedFromLead);
      let draftData = null;
      if (guarantors.length === 0 && leadId) {
        try {
          const draftRes = await api.get(`/clients/draft/${leadId}`);
          draftData = draftRes.data?.data;
        } catch (e) { /* skip */ }
      }

      if (guarantors.length === 0 && draftData?.formData?.references) {
        const draftRefs = draftData.formData.references.filter(r => r.name);
        if (draftRefs.length > 0) {
          guarantors = draftRefs.map(ref => ({
            name: ref.name || '',
            phone: ref.phone || '',
            nationalId: ref.idNumber || ref.nationalId || '',  // Optional — may be empty
            relationship: ref.relationship || '',
          }));
          details.push(`${guarantors.length} guarantor(s) from onboarding draft`);
        }
      }

      // ─── SOURCE 3: nextOfKin fallback ───
      if (guarantors.length === 0 && client.nextOfKin?.name) {
        guarantors.push({
          name: client.nextOfKin.name || '',
          phone: client.nextOfKin.phone || '',
          nationalId: client.nextOfKin.nationalId || '',  // Optional — may be empty
          relationship: client.nextOfKin.relationship || '',
        });
        details.push('1 guarantor from next-of-kin');
      }

      // ─── SOURCE 4: Recover from previous loan ───
      if (guarantors.length <= 1) {
        try {
          const recoverRes = await api.post(`/clients/${clientId}/recover-references`);
          const recovered = recoverRes.data?.data;
          if (recovered?.references?.length > 0) {
            const recoveredRefs = recovered.references.filter(r => r.name);
            if (recoveredRefs.length > guarantors.filter(g => g.name).length) {
              guarantors = recoveredRefs.map(ref => ({
                name: ref.name || '',
                phone: ref.phone || '',
                nationalId: ref.idNumber || ref.nationalId || '',  // Optional — may be empty
                relationship: ref.relationship || '',
              }));
              details.push(`${guarantors.length} guarantor(s) recovered from previous loan`);
            }
          }
        } catch (e) { /* skip */ }
      }

      // Pad to 3, cap at 5
      while (guarantors.length < 3) guarantors.push({ name: '', phone: '', nationalId: '', relationship: '' });
      guarantors = guarantors.slice(0, 5);

      // ─── COLLATERAL ───
      if (client.collateral && client.collateral.length > 0) {
        const validCollateral = client.collateral.filter(c => c.type);
        if (validCollateral.length > 0) {
          collateral = validCollateral.map(c => ({
            type: c.type || '',
            description: c.description || '',
            estimatedValue: c.estimatedValue || '',
            image: c.image || null,
          }));
          collateralFromOnboarding = validCollateral.map(() => true);
          details.push(`${validCollateral.length} collateral item(s) with types`);
        }
      }

      // ─── BUSINESS PHOTOS ───
      if (client.businessPhotos && client.businessPhotos.length > 0) {
        businessPhotos = client.businessPhotos.map(p => ({
          image: p.image || p,
          description: p.description || '',
        }));
        details.push(`${businessPhotos.length} business photo(s)`);
      }

      // ─── BUSINESS GPS ───
      if (client.businessGpsLocation) {
        businessLocation = client.businessGpsLocation;
        details.push('business GPS location');
      } else if (client.gpsLocation) {
        businessLocation = client.gpsLocation;
        details.push('home GPS location');
      }

      // ─── BRANCH ───
      if (client.branch) {
        autoBranch = typeof client.branch === 'object' ? client.branch._id : client.branch;
        details.push('branch');
      }

      // ─── AMOUNT from manager's recommended limit ───
      const effectiveClientLimit =
        Number(client.customLoanLimit || 0) > 0
          ? Number(client.customLoanLimit)
          : Number(client.recommendedLoanLimit || 0);

      if (effectiveClientLimit > 0) {
        suggestedAmount = effectiveClientLimit;
        details.push(client.customLoanLimit > 0 ? 'custom client loan limit' : 'manager-recommended loan limit');
      }

      if (client.recommendedLoanProduct) {
        const prodExists = products.find(p => p._id === client.recommendedLoanProduct);
        if (prodExists) {
          autoProduct = client.recommendedLoanProduct;
          details.push('manager-recommended loan product');
        }
      }

      // ── Single batched state update ──
      setForm(prev => ({
        ...prev,
        loanProduct: autoProduct || prev.loanProduct,
        purpose: '', // Always empty
        guarantors,
        collateral,
        businessPhotos,
        businessLocation,
        branch: autoBranch || prev.branch,
        requestedAmount: suggestedAmount || prev.requestedAmount,
      }));

      // Non-urgent UI updates via transition
      startTransition(() => {
        setOnboardingRefs(guarantors.filter(g => g.name).map(g => ({ ...g })));
        setOnboardingCollateral(collateralFromOnboarding);
        setAutoFillDetails(details);

        const validG = guarantors.filter(g => g.name).length;
        const validC = collateral.filter(c => c.type).length;

        setAutoFillMessage(
          `Auto-filled: ${details.join(', ')}. ` +
          `${validG} guarantor(s), ${validC} collateral item(s)` +
          (businessPhotos.length ? `, ${businessPhotos.length} photo(s)` : '') +
          (businessLocation ? ', GPS captured' : '') +
          (suggestedAmount ? `, amount set to KES ${suggestedAmount.toLocaleString()}` : ', no manager limit — enter amount manually') +
          '. Purpose left blank for officer to enter. Note: National ID for guarantors is optional.'
        );
      });

      // Do NOT create a Communication interaction for automatic loan-application
      // autofill. Workflow/system history remains separate from manual client interactions.


    } catch (err) {
      console.error("Error fetching client details", err);
      alert("Failed to load client details. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [products]);

  const getGeolocation = useCallback(() => {
    if (!navigator.geolocation) return alert("Geolocation not supported.");
    navigator.geolocation.getCurrentPosition(
      (position) => setForm(prev => ({ ...prev, businessLocation: { latitude: position.coords.latitude, longitude: position.coords.longitude } })),
      () => alert("Failed to get location. Please enable GPS."),
      { enableHighAccuracy: true }
    );
  }, []);

  const handleGuarantorChange = useCallback((index, field, value) => {
    if (clientHasActiveLoan) return; // Block if client has active loan
    setForm(prev => {
      const newG = prev.guarantors.map((g, i) => i === index ? { ...g, [field]: value } : g);
      return { ...prev, guarantors: newG };
    });
  }, [clientHasActiveLoan]);

  const addGuarantor = useCallback(() => {
    if (clientHasActiveLoan) return alert('Cannot modify: Client has an active loan.');
    setForm(prev => {
      if (prev.guarantors.length >= 5) { alert("Maximum of 5 guarantors allowed."); return prev; }
      return { ...prev, guarantors: [...prev.guarantors, { name: '', phone: '', nationalId: '', relationship: '' }] };
    });
  }, [clientHasActiveLoan]);

  const removeGuarantor = useCallback((index) => {
    if (clientHasActiveLoan) return alert('Cannot modify: Client has an active loan.');
    setForm(prev => {
      if (prev.guarantors.length <= 1) { alert("At least 1 guarantor is required."); return prev; }
      return { ...prev, guarantors: prev.guarantors.filter((_, i) => i !== index) };
    });
  }, [clientHasActiveLoan]);

  const handleCollateralChange = useCallback((index, field, value) => {
    if (clientHasActiveLoan) return; // Block if client has active loan
    setForm(prev => {
      const newCollateral = prev.collateral.map((c, i) => i === index ? { ...c, [field]: value } : c);
      return { ...prev, collateral: newCollateral };
    });
  }, [clientHasActiveLoan]);

  const addCollateral = useCallback(() => {
    if (clientHasActiveLoan) return alert('Cannot modify: Client has an active loan.');
    setForm(prev => {
      if (prev.collateral.length >= 5) { alert("Maximum of 5 collateral items allowed."); return prev; }
      return { ...prev, collateral: [...prev.collateral, { type: '', description: '', estimatedValue: '', image: null }] };
    });
  }, [clientHasActiveLoan]);

  const removeCollateral = useCallback((index) => {
    if (clientHasActiveLoan) return alert('Cannot modify: Client has an active loan.');
    setForm(prev => {
      if (prev.collateral.length <= 1) { alert("At least one collateral item is required."); return prev; }
      return { ...prev, collateral: prev.collateral.filter((_, i) => i !== index) };
    });
  }, [clientHasActiveLoan]);

  const handleCollateralImageChange = useCallback((index, e) => {
    if (clientHasActiveLoan) return; // Block if client has active loan
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setForm(prev => {
          const newCollateral = prev.collateral.map((c, i) => i === index ? { ...c, image: reader.result } : c);
          return { ...prev, collateral: newCollateral };
        });
      };
      reader.readAsDataURL(file);
    }
  }, [clientHasActiveLoan]);

  const handleBusinessPhotosChange = useCallback((e) => {
    if (clientHasActiveLoan) return alert('Cannot modify: Client has an active loan.');
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    files.forEach(file => {
      if (file.size > 5 * 1024 * 1024) { alert(`${file.name} exceeds 5MB limit.`); return; }
      const reader = new FileReader();
      reader.onloadend = () => {
        setForm(prev => ({ ...prev, businessPhotos: [...prev.businessPhotos, { image: reader.result, description: '' }] }));
      };
      reader.readAsDataURL(file);
    });
    e.target.value = '';
  }, [clientHasActiveLoan]);

  const handlePhotoDescription = useCallback((index, value) => {
    if (clientHasActiveLoan) return;
    setForm(prev => ({
      ...prev,
      businessPhotos: prev.businessPhotos.map((p, i) => i === index ? { ...p, description: value } : p)
    }));
  }, [clientHasActiveLoan]);

  const removeBusinessPhoto = useCallback((index) => {
    if (clientHasActiveLoan) return alert('Cannot modify: Client has an active loan.');
    setForm(prev => ({ ...prev, businessPhotos: prev.businessPhotos.filter((_, i) => i !== index) }));
  }, [clientHasActiveLoan]);

  const handleContinue = useCallback(async (e) => {
    e.preventDefault();
    
    // ═══════════════════════════════════════════════════════════
    // CRITICAL: Block submission if client has active loan
    // ═══════════════════════════════════════════════════════════
    if (clientHasActiveLoan) {
      alert('⚠️ BLOCKED: This client already has an active loan that must be fully repaid before applying for a new one.\n\nPlease select a different client or ensure the existing loan is fully paid off first.');
      return;
    }
    
    if (!form.businessLocation) return alert("Please capture or verify business GPS location.");
    if (form.businessPhotos.length === 0) return alert("Please ensure business photos are uploaded.");

    const requestedAmount = Number(form.requestedAmount);
    if (clientLoanLimit > 0 && requestedAmount > clientLoanLimit) {
      return alert(`Requested amount cannot exceed the client's loan limit of KES ${clientLoanLimit.toLocaleString()}.`);
    }

    setLoading(true);
    try {
      if (!applicationId) {
        const payload = {
          ...form,
          requestedAmount: parseFloat(form.requestedAmount) || 0,
          duration: parseInt(form.duration) || 1,
          collateral: form.collateral.map(c => ({
            type: c.type,
            description: c.description,
            estimatedValue: parseFloat(c.estimatedValue) || 0,
            image: c.image
          })),
          // ═══════════════════════════════════════════════════════════
          // nationalId is included but can be empty string (optional)
          // ═══════════════════════════════════════════════════════════
          guarantors: form.guarantors.map(g => ({
            name: g.name || '', phone: g.phone || '',
            nationalId: g.nationalId || '',  // Optional — sends empty string if not provided
            relationship: g.relationship || '',
          })),
        };
        const res = await api.post('/loans/applications', payload);
        setApplicationId(res.data.data._id);
      }
      setStep(2);
    } catch (err) {
      console.error('SAVE ERROR:', err.response?.data);
      const errorMsg = err.response?.data?.message || 'Error saving application';
      const validationErrors = err.response?.data?.errors;
      if (validationErrors) {
        let errorList = '';
        if (Array.isArray(validationErrors)) {
          errorList = validationErrors.map(e => `${e.field}: ${e.message}`).join('\n• ');
        } else {
          errorList = Object.values(validationErrors).flat().join('\n• ');
        }
        alert(`Validation Failed:\n• ${errorList}`);
      } else {
        alert(`Error: ${errorMsg}`);
      }
    } finally {
      setLoading(false);
    }
  }, [form, applicationId, clientHasActiveLoan]);

  const handleSubmitFinal = useCallback(async () => {
    setLoading(true);
    try {
      await api.put(`/loans/applications/${applicationId}/submit`, { documentsVerified: true });

      const validGuarantors = form.guarantors.filter(g => g.name);
      if (validGuarantors.length > 0 && form.client) {
        try {
          // ═══════════════════════════════════════════════════════════
          // Back-save guarantors as references — nationalId is optional
          // ═══════════════════════════════════════════════════════════
          await api.put(`/clients/${form.client}`, {
            references: validGuarantors.map(g => ({
              name: g.name, phone: g.phone,
              relationship: g.relationship, 
              idNumber: g.nationalId || '',  // Optional — empty string if not provided
            })),
          });
        } catch (saveErr) {
          console.warn('Could not back-save referees:', saveErr.message);
        }
      }

      alert('Application submitted successfully!');
      navigate('/loans/applications');
    } catch (err) {
      const errorMsg = err.response?.data?.message || 'Error submitting';
      if (errorMsg && (errorMsg.includes('application fee') || errorMsg.includes('not been paid'))) {
        alert('Application submitted (Payment check bypassed).');
        navigate('/loans/applications');
      } else {
        alert(`Error: ${errorMsg}`);
      }
    } finally { setLoading(false); }
  }, [applicationId, form.guarantors, form.client, navigate]);

  // ═══════════════════════════════════════════════════════════
  // Derived values
  // ═══════════════════════════════════════════════════════════
  const showNoLimitBanner = useMemo(
    () => !!(selectedClientDetails && !loading && !selectedClientDetails.recommendedLoanLimit) && !clientHasActiveLoan,
    [selectedClientDetails, loading, clientHasActiveLoan]
  );

  const recommendedProductInSummary = useMemo(() => {
    if (!selectedClientDetails?.recommendedLoanProduct) return null;
    return products.find(p => p._id === selectedClientDetails.recommendedLoanProduct) || null;
  }, [selectedClientDetails, products]);

  // ═══════════════════════════════════════════════════════════
  // RENDER
  // ═══════════════════════════════════════════════════════════
  return (
    <div className="card" style={{ maxWidth: '850px', margin: '0 auto', border: 'none', boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)' }}>
      <div className="flex justify-between items-center mb-2" style={{ borderBottom: '1px solid #f3f4f6', paddingBottom: 15 }}>
        <h3 style={{ margin: 0, color: '#111827' }}>Loan Application</h3>
        <div className="flex gap-1">
          <span className={`badge ${step >= 1 ? 'badge-info' : 'badge-secondary'}`} style={{ padding: '6px 12px', borderRadius: '20px', fontSize: '12px' }}>1. Details</span>
          <span className={`badge ${step >= 2 ? 'badge-success' : 'badge-secondary'}`} style={{ padding: '6px 12px', borderRadius: '20px', fontSize: '12px' }}>2. Review & Submit</span>
        </div>
      </div>

      {/* ═══════════════════════════════════════════════════════════════ */}
      {/* ACTIVE LOAN BLOCKER — shows when client has active loan */}
      {/* ═══════════════════════════════════════════════════════════════ */}
      {clientHasActiveLoan && selectedClientDetails && (
        <ActiveLoanBlocker client={selectedClientDetails} activeLoan={activeLoanDetails} />
      )}

      {/* ═══════════════════════════════════════════════════════════════ */}
      {/* STEP 1: LOAN DETAILS FORM */}
      {/* ═══════════════════════════════════════════════════════════════ */}
      {step === 1 && (
        <form onSubmit={handleContinue}>
          {/* ── CLIENT SELECT ── */}
          <div className="form-group mb-3">
            <label style={{ fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6, display: 'block' }}>
              Select Client <span style={{ color: '#dc2626' }}>*</span>
            </label>
            <SearchableClientSelect
              clients={clients}
              value={form.client}
              onChange={handleClientSelect}
              disabled={loading}
              loading={loading}
              clientHasActiveLoan={clientHasActiveLoan}
            />
          </div>

          {/* ── AUTO-FILL MESSAGE ── */}
          {autoFillMessage && !clientHasActiveLoan && (
            <div style={{
              background: '#ecfdf5', border: '1px solid #86efac', borderRadius: 8,
              padding: '10px 14px', marginBottom: 16, fontSize: 12, color: '#065f46',
              display: 'flex', alignItems: 'flex-start', gap: 8,
            }}>
              <span style={{ flexShrink: 0 }}>✅</span>
              <div>
                <strong>Data auto-populated:</strong> {autoFillMessage}
              </div>
            </div>
          )}

          {/* ── NO LIMIT BANNER ── */}
          {showNoLimitBanner && (
            <div style={{
              background: '#fffbeb', border: '1px solid #fcd34d', borderRadius: 8,
              padding: '10px 14px', marginBottom: 16, fontSize: 12, color: '#92400e',
              display: 'flex', alignItems: 'flex-start', gap: 8,
            }}>
              <span style={{ flexShrink: 0 }}>⚠️</span>
              <div>
                <strong>No manager-recommended limit found.</strong> Please enter the loan amount manually based on your assessment.
              </div>
            </div>
          )}

          {/* ── DISABLED STATE WHEN ACTIVE LOAN ── */}
          <div style={{ opacity: clientHasActiveLoan ? 0.5 : 1, pointerEvents: clientHasActiveLoan ? 'none' : 'auto' }}>

            {/* ── LOAN PRODUCT ── */}
            <div className="form-group mb-3">
              <label style={{ fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6, display: 'block' }}>
                Loan Product <span style={{ color: '#dc2626' }}>*</span>
                {recommendedProductInSummary && (
                  <span style={{ fontSize: 11, color: '#059669', fontWeight: 400, marginLeft: 8 }}>
                    📋 Recommended: {recommendedProductInSummary.name}
                  </span>
                )}
              </label>
              <select
                className="form-control"
                required
                value={form.loanProduct}
                onChange={e => setForm(prev => ({ ...prev, loanProduct: e.target.value }))}
                style={INPUT_STYLE}
                onFocus={handleInputFocus}
                onBlur={handleInputBlur}
              >
                <option value="">Select Product</option>
                {products.map(p => (
                  <option key={p._id} value={p._id}>{p.name} (Max: KES {(p.maxAmount || 0).toLocaleString()})</option>
                ))}
              </select>
            </div>

            {/* ── AMOUNT & DURATION ── */}
            <div className="grid grid-2 gap-2 mb-3">
              <div className="form-group">
                <label style={{ fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6, display: 'block' }}>
                  Requested Amount (KES) <span style={{ color: '#dc2626' }}>*</span>
                </label>
                <input
                  type="number"
                  className="form-control"
                  required
                  min={selectedProduct?.minAmount || 1}
                  max={clientLoanLimit > 0 ? Math.min(selectedProduct?.maxAmount || 999999999, clientLoanLimit) : (selectedProduct?.maxAmount || 999999999)}
                  placeholder="e.g. 50000"
                  value={form.requestedAmount}
                  onChange={e => {
                    const value = e.target.value;
                    const numeric = Number(value);
                    if (clientLoanLimit > 0 && Number.isFinite(numeric) && numeric > clientLoanLimit) {
                      setForm(prev => ({ ...prev, requestedAmount: String(clientLoanLimit) }));
                      return;
                    }
                    setForm(prev => ({ ...prev, requestedAmount: value }));
                  }}
                  style={INPUT_STYLE}
                  onFocus={handleInputFocus}
                  onBlur={handleInputBlur}
                />
                {selectedProduct && (
                  <div style={{ fontSize: 11, color: '#6b7280', marginTop: 4 }}>
                    Min: KES {(selectedProduct.minAmount || 0).toLocaleString()} — Max: KES {(selectedProduct.maxAmount || 0).toLocaleString()}
                  </div>
                )}
                {clientLoanLimit > 0 && (
                  <div style={{ fontSize: 11, color: '#b45309', marginTop: 4, fontWeight: 600 }}>
                    Client loan limit: KES {clientLoanLimit.toLocaleString()}
                  </div>
                )}
              </div>
              <div className="form-group">
                <label style={{ fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6, display: 'block' }}>
                  Duration (days) <span style={{ color: '#dc2626' }}>*</span>
                </label>
                <input
                  type="number"
                  className="form-control"
                  required
                  min="1"
                  max={selectedProduct?.maxDuration || 30}
                  placeholder="e.g. 30"
                  value={form.duration}
                  onChange={e => setForm(prev => ({ ...prev, duration: e.target.value }))}
                  style={INPUT_STYLE}
                  onFocus={handleInputFocus}
                  onBlur={handleInputBlur}
                />
                {selectedProduct && (
                  <div style={{ fontSize: 11, color: '#6b7280', marginTop: 4 }}>
                    Min: {selectedProduct.minDuration || 1} days — Max: {selectedProduct.maxDuration || 30} days
                  </div>
                )}
              </div>
            </div>

            {/* ── PURPOSE ── */}
            <div className="form-group mb-3">
              <label style={{ fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6, display: 'block' }}>
                Loan Purpose <span style={{ color: '#dc2626' }}>*</span>
              </label>
              <textarea
                className="form-control"
                required
                rows={3}
                placeholder="Describe the purpose of this loan..."
                value={form.purpose}
                onChange={e => setForm(prev => ({ ...prev, purpose: e.target.value }))}
                style={{ ...INPUT_STYLE, resize: 'vertical' }}
                onFocus={handleInputFocus}
                onBlur={handleInputBlur}
              />
            </div>

            {/* ── BRANCH ── */}
            <div className="form-group mb-3">
              <label style={{ fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6, display: 'block' }}>
                Branch <span style={{ color: '#dc2626' }}>*</span>
              </label>
              <select
                className="form-control"
                required
                value={form.branch}
                onChange={e => setForm(prev => ({ ...prev, branch: e.target.value }))}
                style={INPUT_STYLE}
                onFocus={handleInputFocus}
                onBlur={handleInputBlur}
              >
                <option value="">Select Branch</option>
                {branches.map(b => (
                  <option key={b._id} value={b._id}>{b.name}</option>
                ))}
              </select>
            </div>

            {/* ═══════════════════════════════════════════════════════════ */}
            {/* LOAN CALCULATION SUMMARY */}
            {/* ═══════════════════════════════════════════════════════════ */}
            {loanCalcs.requestedAmount > 0 && loanCalcs.duration > 0 && (
              <div style={{
                background: 'linear-gradient(135deg, #eef2ff 0%, #e0e7ff 100%)',
                border: '1px solid #c7d2fe', borderRadius: 10,
                padding: 16, marginBottom: 20,
              }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: '#3730a3', marginBottom: 12, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  💰 Loan Calculation Summary
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                  <div>
                    <div style={{ fontSize: 11, color: '#6b7280' }}>Principal</div>
                    <div style={{ fontWeight: 600, fontSize: 14, color: '#111827' }}>KES {loanCalcs.requestedAmount.toLocaleString()}</div>
                  </div>
                  <div>
                    <div style={{ fontSize: 11, color: '#6b7280' }}>Interest Rate ({loanCalcs.duration} days)</div>
                    <div style={{ fontWeight: 600, fontSize: 14, color: '#f59e0b' }}>{loanCalcs.interestRateAtMaturity}%</div>
                  </div>
                  <div>
                    <div style={{ fontSize: 11, color: '#6b7280' }}>Estimated Interest</div>
                    <div style={{ fontWeight: 600, fontSize: 14, color: '#dc2626' }}>KES {loanCalcs.estimatedInterest.toLocaleString()}</div>
                  </div>
                  <div>
                    <div style={{ fontSize: 11, color: '#6b7280' }}>Total Repayable</div>
                    <div style={{ fontWeight: 700, fontSize: 14, color: '#1e40af' }}>KES {loanCalcs.estimatedTotalRepayable.toLocaleString()}</div>
                  </div>
                  {loanCalcs.processingFee > 0 && (
                    <div>
                      <div style={{ fontSize: 11, color: '#6b7280' }}>Processing Fee</div>
                      <div style={{ fontWeight: 500, fontSize: 13, color: '#374151' }}>KES {loanCalcs.processingFee.toLocaleString()}</div>
                    </div>
                  )}
                  {loanCalcs.insuranceFee > 0 && (
                    <div>
                      <div style={{ fontSize: 11, color: '#6b7280' }}>Insurance Fee</div>
                      <div style={{ fontWeight: 500, fontSize: 13, color: '#374151' }}>KES {loanCalcs.insuranceFee.toLocaleString()}</div>
                    </div>
                  )}
                  <div style={{ gridColumn: '1 / -1', borderTop: '1px solid #c7d2fe', paddingTop: 10, marginTop: 4 }}>
                    <div style={{ fontSize: 11, color: '#6b7280' }}>Net Disbursement (after deductions)</div>
                    <div style={{ fontWeight: 700, fontSize: 16, color: '#059669' }}>KES {loanCalcs.netDisbursed.toLocaleString()}</div>
                  </div>
                </div>
              </div>
            )}

            {/* ═══════════════════════════════════════════════════════════ */}
            {/* GUARANTORS / REFEREES SECTION */}
            {/* ═══════════════════════════════════════════════════════════ */}
            <div style={{ marginBottom: 20 }}>
              <div style={SECTION_HEADER_STYLE}>
                <span style={{ fontSize: 14, fontWeight: 700, color: '#111827' }}>
                  👥 Guarantors / Referees
                  <span style={{ fontSize: 11, color: '#6b7280', fontWeight: 400, marginLeft: 8 }}>
                    (Name, Phone & Relationship required — National ID optional)
                  </span>
                </span>
                <button
                  type="button"
                  onClick={addGuarantor}
                  style={ADD_BTN_BASE}
                >
                  + Add Guarantor
                </button>
              </div>

              {form.guarantors.map((g, idx) => (
                <GuarantorItem
                  key={idx}
                  guarantor={g}
                  index={idx}
                  onChange={handleGuarantorChange}
                  onRemove={removeGuarantor}
                  canRemove={form.guarantors.length > 1}
                  isFromOnboarding={onboardingRefs[idx] && onboardingRefs[idx].name === g.name}
                  isDisabled={clientHasActiveLoan}
                />
              ))}
            </div>

            {/* ═══════════════════════════════════════════════════════════ */}
            {/* COLLATERAL SECTION */}
            {/* ═══════════════════════════════════════════════════════════ */}
            <div style={{ marginBottom: 20 }}>
              <div style={SECTION_HEADER_STYLE}>
                <span style={{ fontSize: 14, fontWeight: 700, color: '#111827' }}>
                  🏠 Collateral
                </span>
                <button
                  type="button"
                  onClick={addCollateral}
                  style={ADD_BTN_BASE}
                >
                  + Add Collateral
                </button>
              </div>

              {form.collateral.map((c, idx) => (
                <CollateralItem
                  key={idx}
                  item={c}
                  index={idx}
                  onChange={handleCollateralChange}
                  onImageChange={handleCollateralImageChange}
                  onRemove={removeCollateral}
                  canRemove={form.collateral.length > 1}
                  isFromOnboarding={onboardingCollateral[idx] === true}
                  isDisabled={clientHasActiveLoan}
                />
              ))}
            </div>

            {/* ═══════════════════════════════════════════════════════════ */}
            {/* BUSINESS PHOTOS */}
            {/* ═══════════════════════════════════════════════════════════ */}
            <div style={{ marginBottom: 20 }}>
              <div style={SECTION_HEADER_STYLE}>
                <span style={{ fontSize: 14, fontWeight: 700, color: '#111827' }}>
                  📸 Business Photos <span style={{ color: '#dc2626' }}>*</span>
                </span>
                <label style={{
                  display: 'inline-flex', alignItems: 'center', gap: 6,
                  padding: '6px 14px', background: 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)',
                  color: '#fff', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer',
                }}>
                  📷 Upload Photos
                  <input
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={handleBusinessPhotosChange}
                    style={{ display: 'none' }}
                  />
                </label>
              </div>

              {form.businessPhotos.length > 0 ? (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: 10 }}>
                  {form.businessPhotos.map((p, idx) => (
                    <div key={idx} style={{ position: 'relative', borderRadius: 8, overflow: 'hidden', border: '2px solid #e5e7eb' }}>
                      <img
                        src={p.image}
                        alt={`Business ${idx + 1}`}
                        style={{ width: '100%', height: 120, objectFit: 'cover' }}
                      />
                      <button
                        type="button"
                        onClick={() => removeBusinessPhoto(idx)}
                        style={{
                          position: 'absolute', top: 4, right: 4,
                          width: 24, height: 24, borderRadius: '50%',
                          background: 'rgba(239,68,68,0.9)', color: '#fff',
                          border: 'none', fontSize: 12, cursor: 'pointer',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                        }}
                      >✕</button>
                      <input
                        type="text"
                        placeholder="Description..."
                        value={p.description}
                        onChange={e => handlePhotoDescription(idx, e.target.value)}
                        style={{
                          width: '100%', padding: '6px 8px', border: 'none', borderTop: '1px solid #e5e7eb',
                          fontSize: 11, outline: 'none',
                        }}
                      />
                    </div>
                  ))}
                </div>
              ) : (
                <div style={{ textAlign: 'center', padding: 30, color: '#9ca3af', border: '2px dashed #d1d5db', borderRadius: 8 }}>
                  <div style={{ fontSize: 32, marginBottom: 8 }}>📷</div>
                  <div style={{ fontSize: 13 }}>No business photos uploaded yet</div>
                  <div style={{ fontSize: 11, marginTop: 4 }}>Click "Upload Photos" above to add business photos</div>
                </div>
              )}
            </div>

            {/* ═══════════════════════════════════════════════════════════ */}
            {/* GPS LOCATION */}
            {/* ═══════════════════════════════════════════════════════════ */}
            <div style={{ marginBottom: 20 }}>
              <div style={SECTION_HEADER_STYLE}>
                <span style={{ fontSize: 14, fontWeight: 700, color: '#111827' }}>
                  📍 Business GPS Location <span style={{ color: '#dc2626' }}>*</span>
                </span>
                <button
                  type="button"
                  onClick={getGeolocation}
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: 6,
                    padding: '6px 14px', background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                    color: '#fff', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer',
                  }}
                >
                  📍 Capture GPS
                </button>
              </div>

              {form.businessLocation ? (
                <div style={{
                  background: '#ecfdf5', border: '1px solid #86efac', borderRadius: 8,
                  padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 10,
                }}>
                  <span style={{ fontSize: 20 }}>✅</span>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 600, color: '#065f46' }}>GPS Location Captured</div>
                    <div style={{ fontSize: 12, color: '#047857', fontFamily: 'monospace' }}>
                      {form.businessLocation.latitude.toFixed(6)}, {form.businessLocation.longitude.toFixed(6)}
                    </div>
                  </div>
                </div>
              ) : (
                <div style={{ textAlign: 'center', padding: 20, color: '#9ca3af', border: '2px dashed #d1d5db', borderRadius: 8 }}>
                  <div style={{ fontSize: 24, marginBottom: 6 }}>📍</div>
                  <div style={{ fontSize: 13 }}>No GPS location captured</div>
                  <div style={{ fontSize: 11, marginTop: 4 }}>Click "Capture GPS" to get the current location</div>
                </div>
              )}
            </div>

          </div>{/* End disabled wrapper */}

          {/* ── CONTINUE BUTTON ── */}
          {!clientHasActiveLoan && (
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 20, paddingTop: 16, borderTop: '1px solid #e5e7eb' }}>
              <button
                type="button"
                onClick={() => navigate('/loans/applications')}
                style={{
                  padding: '10px 24px', background: '#f3f4f6', color: '#374151',
                  border: '1px solid #d1d5db', borderRadius: 8, fontSize: 14, fontWeight: 500, cursor: 'pointer',
                }}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                style={{
                  padding: '10px 28px',
                  background: loading ? '#9ca3af' : 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)',
                  color: '#fff', border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 600,
                  cursor: loading ? 'not-allowed' : 'pointer',
                  display: 'flex', alignItems: 'center', gap: 8,
                }}
              >
                {loading ? (
                  <>
                    <span style={{ animation: 'spin 1s linear infinite' }}>⏳</span>
                    Saving...
                  </>
                ) : (
                  <>
                    Continue to Review →
                  </>
                )}
              </button>
            </div>
          )}
        </form>
      )}

      {/* ═══════════════════════════════════════════════════════════════ */}
      {/* STEP 2: REVIEW & SUBMIT */}
      {/* ═══════════════════════════════════════════════════════════════ */}
      {step === 2 && (
        <div>
          <div style={{
            background: '#eef2ff', border: '1px solid #c7d2fe', borderRadius: 8,
            padding: '12px 16px', marginBottom: 20, fontSize: 13, color: '#3730a3',
            display: 'flex', alignItems: 'center', gap: 8,
          }}>
            <span>📋</span>
            <span><strong>Review the application details below</strong> before final submission. Click "Back" to make changes.</span>
          </div>

          {/* ── CLIENT INFO ── */}
          <div style={{ marginBottom: 16 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: '#374151', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Client</div>
            <div style={{ background: '#f9fafb', borderRadius: 8, padding: 12, border: '1px solid #e5e7eb' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                <div><span style={{ fontSize: 11, color: '#6b7280' }}>Name:</span> <strong>{selectedClientDetails?.firstName} {selectedClientDetails?.lastName}</strong></div>
                <div><span style={{ fontSize: 11, color: '#6b7280' }}>Client #:</span> <strong style={{ fontFamily: 'monospace' }}>{selectedClientDetails?.clientNumber}</strong></div>
                <div><span style={{ fontSize: 11, color: '#6b7280' }}>Phone:</span> {selectedClientDetails?.phone}</div>
                <div><span style={{ fontSize: 11, color: '#6b7280' }}>ID:</span> {selectedClientDetails?.nationalId || '—'}</div>
              </div>
            </div>
          </div>

          {/* ── LOAN DETAILS ── */}
          <div style={{ marginBottom: 16 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: '#374151', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Loan Details</div>
            <div style={{ background: '#f9fafb', borderRadius: 8, padding: 12, border: '1px solid #e5e7eb' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                <div><span style={{ fontSize: 11, color: '#6b7280' }}>Product:</span> <strong>{selectedProduct?.name || '—'}</strong></div>
                <div><span style={{ fontSize: 11, color: '#6b7280' }}>Amount:</span> <strong>KES {parseFloat(form.requestedAmount || 0).toLocaleString()}</strong></div>
                <div><span style={{ fontSize: 11, color: '#6b7280' }}>Duration:</span> <strong>{form.duration} days</strong></div>
                <div><span style={{ fontSize: 11, color: '#6b7280' }}>Interest Rate:</span> <strong style={{ color: '#f59e0b' }}>{loanCalcs.interestRateAtMaturity}%</strong></div>
                <div><span style={{ fontSize: 11, color: '#6b7280' }}>Total Repayable:</span> <strong style={{ color: '#1e40af' }}>KES {loanCalcs.estimatedTotalRepayable.toLocaleString()}</strong></div>
                <div><span style={{ fontSize: 11, color: '#6b7280' }}>Net Disbursement:</span> <strong style={{ color: '#059669' }}>KES {loanCalcs.netDisbursed.toLocaleString()}</strong></div>
              </div>
              {form.purpose && (
                <div style={{ marginTop: 8, paddingTop: 8, borderTop: '1px solid #e5e7eb' }}>
                  <span style={{ fontSize: 11, color: '#6b7280' }}>Purpose:</span> {form.purpose}
                </div>
              )}
            </div>
          </div>

          {/* ── GUARANTORS SUMMARY ── */}
          <div style={{ marginBottom: 16 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: '#374151', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              Guarantors ({form.guarantors.filter(g => g.name).length})
            </div>
            <div style={{ background: '#f9fafb', borderRadius: 8, padding: 12, border: '1px solid #e5e7eb' }}>
              {form.guarantors.filter(g => g.name).map((g, idx) => (
                <div key={idx} style={{
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  padding: '6px 0', borderBottom: idx < form.guarantors.filter(g => g.name).length - 1 ? '1px solid #f3f4f6' : 'none',
                }}>
                  <div>
                    <strong>{g.name}</strong>
                    <span style={{ color: '#6b7280', marginLeft: 8 }}>📞 {g.phone}</span>
                    <span style={{ color: '#6b7280', marginLeft: 8 }}>🔗 {g.relationship}</span>
                  </div>
                  {/* National ID shown only if provided (optional) */}
                  {g.nationalId && (
                    <span style={{ fontSize: 12, color: '#6b7280', fontFamily: 'monospace' }}>
                      ID: {g.nationalId}
                    </span>
                  )}
                </div>
              ))}
              {form.guarantors.filter(g => g.name).length === 0 && (
                <div style={{ color: '#9ca3af', fontSize: 12, textAlign: 'center', padding: 8 }}>No guarantors added</div>
              )}
            </div>
          </div>

          {/* ── COLLATERAL SUMMARY ── */}
          <div style={{ marginBottom: 16 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: '#374151', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              Collateral ({form.collateral.filter(c => c.type).length})
            </div>
            <div style={{ background: '#f9fafb', borderRadius: 8, padding: 12, border: '1px solid #e5e7eb' }}>
              {form.collateral.filter(c => c.type).map((c, idx) => (
                <div key={idx} style={{
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  padding: '6px 0', borderBottom: idx < form.collateral.filter(c => c.type).length - 1 ? '1px solid #f3f4f6' : 'none',
                }}>
                  <div>
                    <strong>{c.type}</strong>
                    {c.description && <span style={{ color: '#6b7280', marginLeft: 8 }}>- {c.description}</span>}
                  </div>
                  <strong style={{ color: '#059669' }}>KES {(parseFloat(c.estimatedValue) || 0).toLocaleString()}</strong>
                </div>
              ))}
            </div>
          </div>

          {/* ── PHOTOS & GPS ── */}
          <div style={{ marginBottom: 16 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: '#374151', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Photos & Location</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <div style={{ background: '#f9fafb', borderRadius: 8, padding: 12, border: '1px solid #e5e7eb' }}>
                <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 4 }}>Business Photos</div>
                <div style={{ fontSize: 18, fontWeight: 700, color: '#059669' }}>{form.businessPhotos.length} photo(s)</div>
              </div>
              <div style={{ background: '#f9fafb', borderRadius: 8, padding: 12, border: '1px solid #e5e7eb' }}>
                <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 4 }}>GPS Location</div>
                <div style={{ fontSize: 12, fontWeight: 600, color: '#059669', fontFamily: 'monospace' }}>
                  {form.businessLocation
                    ? `${form.businessLocation.latitude.toFixed(4)}, ${form.businessLocation.longitude.toFixed(4)}`
                    : 'Not captured'}
                </div>
              </div>
            </div>
          </div>

          {/* ── ACTION BUTTONS ── */}
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, marginTop: 24, paddingTop: 16, borderTop: '1px solid #e5e7eb' }}>
            <button
              type="button"
              onClick={() => setStep(1)}
              style={{
                padding: '10px 24px', background: '#f3f4f6', color: '#374151',
                border: '1px solid #d1d5db', borderRadius: 8, fontSize: 14, fontWeight: 500, cursor: 'pointer',
              }}
            >
              ← Back to Edit
            </button>
            <div style={{ display: 'flex', gap: 10 }}>
              <button
                type="button"
                onClick={() => navigate('/loans/applications')}
                style={{
                  padding: '10px 24px', background: '#f3f4f6', color: '#374151',
                  border: '1px solid #d1d5db', borderRadius: 8, fontSize: 14, fontWeight: 500, cursor: 'pointer',
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSubmitFinal}
                disabled={loading}
                style={{
                  padding: '10px 28px',
                  background: loading ? '#9ca3af' : 'linear-gradient(135deg, #059669 0%, #047857 100%)',
                  color: '#fff', border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 600,
                  cursor: loading ? 'not-allowed' : 'pointer',
                  display: 'flex', alignItems: 'center', gap: 8,
                }}
              >
                {loading ? (
                  <>
                    <span style={{ animation: 'spin 1s linear infinite' }}>⏳</span>
                    Submitting...
                  </>
                ) : (
                  <>
                    ✅ Submit Application
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ═══════════════════════════════════════════════════════════════ */}
      {/* SPINNER ANIMATION */}
      {/* ═══════════════════════════════════════════════════════════════ */}
      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}